import type { FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { ThemeOverride, ThemeSurface } from '@wix/da-react-context/pkg/ThemeContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import Loader from '@wix/da-ds/pkg/Loader';
import { useFileUpload } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { useUnlockVideoUploadsCheck } from '@wix/da-submit-shared/pkg/modals/UnlockWithCoreUpsellModal/useUnlockVideoUploadsCheck';
import StudioTableBatchBanner from '../StudioTableBatchBanner';
import StudioTableHeader from '../StudioTableHeader';
import StudioPagination from '../StudioPagination';
import { useDraftsSelect } from '../../hooks/useDraftsSelect';
import { STUDIO_DEVIATIONS_STREAM } from '../../../constants';
import { StudioSection, TableLayout } from '../../types/store';
import { useStudioUploadOptions } from '../../hooks/useStudioUploadOptions';
import StudioDeviationList from '../StudioDeviationList';
import StudioDeviationGrid from '../StudioDeviationGrid';

import s from './StudioTableDesktop.scss';

export interface Props {
  tableLayout: TableLayout;
  activeSection: StudioSection;
  canDropFiles?: boolean;
  isGridLayoutSupported?: boolean;
}

const StudioTableDesktop: FC<Props> = ({
  tableLayout,
  activeSection,
  canDropFiles,
  isGridLayoutSupported,
}) => {
  const { t } = useTranslation();

  const { streamId, streamConfig, streamThrottle } = STUDIO_DEVIATIONS_STREAM;
  const { items: deviations, isFetching } = useNormalizedDeviationStream(
    streamId,
    streamThrottle,
    streamConfig
  );

  const selectProps = useDraftsSelect(deviations);
  const { selectedCount, allIsSelected, onSelectAll } = selectProps;

  const lockedVideoUploadOptions = useUnlockVideoUploadsCheck();
  const { input, getDropzoneRootProps, isDragging } = useFileUpload(
    useStudioUploadOptions({
      allowClickOnRoot: false,
      ...lockedVideoUploadOptions,
    })
  );

  return (
    <div className={cn(s['root'], s[`section-${activeSection.toLowerCase()}`])}>
      <div className={s['sticky-header']}>
        <StudioTableHeader
          selectedCount={selectedCount}
          allIsSelected={allIsSelected}
          onSelect={onSelectAll}
        />
        <StudioTableBatchBanner />
      </div>

      <div className={s['table-area']} {...(canDropFiles ? getDropzoneRootProps() : undefined)}>
        <ThemeOverride themeSurface={ThemeSurface.SECONDARY} tag="div">
          {tableLayout === 'grid' && isGridLayoutSupported ? (
            <BiLoggerContextProvider value={{ sectionname: 'studio-grid' }}>
              <StudioDeviationGrid deviations={deviations} selectProps={selectProps} />
            </BiLoggerContextProvider>
          ) : (
            <BiLoggerContextProvider value={{ sectionname: 'studio-list' }}>
              <StudioDeviationList deviations={deviations} selectProps={selectProps} />
            </BiLoggerContextProvider>
          )}
        </ThemeOverride>

        <div className={s['dropzone-expander']}>{canDropFiles && t('studio.table.dropzone')}</div>

        <div className={s['pagination']}>
          <StudioPagination onFetchSuccess={() => window.scrollTo({ top: 0 })} />
        </div>

        {isDragging && (
          <div className={s['dragging']}>
            <div className={s['sticky']}>
              <Loader state="static" size="big" />
              <div className={s['dragging-title']}>{t('uploadZone.drop.title')}</div>
            </div>
          </div>
        )}

        {!isDragging && isFetching && (
          <div className={s['loading']}>
            <Loader className={s['sticky']} state="loading" size="big" />
          </div>
        )}
      </div>

      {canDropFiles && <>{input}</>}
    </div>
  );
};
StudioTableDesktop.displayName = 'StudioTableDesktop';

export default StudioTableDesktop;
